import React, { useEffect, useMemo, useState } from "react"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import ModalPortal from "../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { faClose, faUsers, faRemove, faUserPlus } from "@fortawesome/free-solid-svg-icons"
import { ButtonIcon } from "../../../components/Button/Button"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { useTranslation } from "react-i18next"
import GetSpace from "../../../utils/space"
import { EAwardApplyTargetType } from "../../../Enum/EAwardApplyTargetType"
import { useGetCustomer } from "../../../hooks/Costumer/useGetCustomer"
import { customerMemory } from "../../../Objects/Memory/Memory"
import Button from "../../../components/Button/Button"
import { ApplyAwardRequest } from "../../../hooks/Award/useApplyAward"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ListSelections } from "../../../components/ListSelections/ListSelections"
import { CountElements } from "../../../components/CountElements/CountElements"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"

interface AwardApplyPopupProps {
  title: string
  actionLabel: string
  helpTitle: string
  helpDescription: string
  helpBullets: string[]
  helpKey: string
  onClose: () => void
  onConfirm: (request: ApplyAwardRequest) => Promise<void>
}

type OptionValue = "all" | "men" | "women" | "selected"

export const AwardApplyPopup: React.FC<AwardApplyPopupProps> = ({
  title,
  actionLabel,
  helpTitle,
  helpDescription,
  helpBullets,
  helpKey,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation("awards")

  const { handleGetCostumer } = useGetCustomer()

  const [targetType, setTargetType] = useState<OptionValue>("all")

  const [selectedCustomerIds, setSelectedCustomerIds] = useState<number[]>([])

  const [enableCustomerSelection, setEnableCustomerSelection] = useState(false)

  useEffect(() => {
    handleGetCostumer(() => {})
  }, [])

  const options = useMemo(
    () => [
      { label: t("apply_target_all"), value: "all" },
      { label: t("apply_target_men"), value: "men" },
      { label: t("apply_target_women"), value: "women" },
      { label: t("apply_target_selected_customers"), value: "selected" },
    ],
    [t],
  )

  const handleChangeTarget = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value as OptionValue
    setTargetType(value)

    if (value !== "selected") {
      setSelectedCustomerIds([])
    }
  }

  const handleAddCustomer = (id: number) => {
    setSelectedCustomerIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
  }

  const handleRemoveCustomer = (id: number) => {
    setSelectedCustomerIds((prev) => prev.filter((customerId) => customerId !== id))
  }

  const getRequest = (): ApplyAwardRequest => {
    switch (targetType) {
      case "men":
        return { targetType: EAwardApplyTargetType.Men, customerIds: [] }
      case "women":
        return { targetType: EAwardApplyTargetType.Women, customerIds: [] }
      case "selected":
        return { targetType: EAwardApplyTargetType.SelectedCustomers, customerIds: selectedCustomerIds }
      default:
        return { targetType: EAwardApplyTargetType.All, customerIds: [] }
    }
  }

  const selectedCustomers = selectedCustomerIds
    .map((id) => customerMemory.findById(id))
    .filter((customer) => !!customer && !customer.isDefaultCustomer)

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={title}
              icon={faUsers}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey={helpKey}
                    title={helpTitle}
                    variant="global"
                    description={helpDescription}
                    bullets={helpBullets}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.serivesDir.awards.awardApplyPopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <div className="p-2">
              <div className="rounded-xl border border-orange-100 bg-orange-50/40 p-3">
                <GenericDropDown
                  legend={t("apply_target_legend")}
                  data={options}
                  initialState={targetType}
                  handlerFuncion={handleChangeTarget}
                  descriptionKey="interactionDescriptions:pages.serivesDir.awards.awardApplyPopup.select.applyTargetLegend"
                />
              </div>

              {targetType === "selected" && (
                <>
                  <GetSpace top={10} />

                  <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-slate-700">{t("apply_select_customers_action")}</span>

                      <ButtonIcon
                        typeButton="button"
                        type="primary"
                        size="medium"
                        icon={faUserPlus}
                        sizeNumber={SIZE.buttonCloseIconSize}
                        bgColor={THEME.colors.buttonBGPlusColor}
                        iconColor={THEME.colors.buttonFGPlusColor}
                        onClick={() => setEnableCustomerSelection(true)}
                        descriptionKey="interactionDescriptions:pages.serivesDir.awards.awardApplyPopup.button.setEnableCustomerSelectionTrue"
                      />
                    </div>
                  </div>

                  <GetSpace top={10} />

                  <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                    <span className="text-sm font-bold text-slate-700">{t("apply_selected_customers")}</span>

                    <GetSpace top={5} />

                    <CountElements count={selectedCustomers.length} title={t("apply_selected_customers")} />

                    <GetSpace top={5} />

                    {selectedCustomers.length === 0 && (
                      <span className="text-xs text-slate-500">{t("apply_no_selected_customers")}</span>
                    )}

                    {selectedCustomers.map((customer) => (
                      <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
                        <div
                          key={customer!.id}
                          className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2"
                        >
                          <span
                            className="min-w-0 flex-1 truncate text-sm font-medium text-slate-700"
                            title={customer!.personEntity?.name ?? "-"}
                          >
                            {customer!.personEntity?.name ?? "-"}
                          </span>

                          <button
                            className="rounded-full bg-red-50 p-1.5 transition-colors hover:bg-red-100"
                            type="button"
                            onClick={() => handleRemoveCustomer(customer!.id)}
                            title={t("buttons:button_remove")}
                            data-interaction-description-key="interactionDescriptions:pages.serivesDir.awards.awardApplyPopup.button.remove"
                          >
                            <FontAwesomeIcon icon={faRemove} color={THEME.colors.buttonBGRemoveColor} />
                          </button>
                        </div>
                      </Animate>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <div className={defaultStyle.buttonsController}>
              <Button
                label={actionLabel}
                typeButton="button"
                type="primary"
                size="medium"
                bgColor={THEME.colors.buttonBGSaveColor}
                onClick={() => onConfirm(getRequest())}
                descriptionKey="interactionDescriptions:pages.serivesDir.awards.awardApplyPopup.button.actionLabel"
              />
            </div>
          </div>
        </Animate>
      </div>

      {enableCustomerSelection && (
        <ListSelections
          title={t("apply_target_selected_customers")}
          icon={faUsers}
          listCards={customerMemory
            .getListCards(true)
            .filter((card) => !customerMemory.findById(card.id)?.isDefaultCustomer)}
          messageCount={t("list:label_customer_page_message_count")}
          messageEmpty={t("list:label_customer_page_message_empty")}
          onClinck={(id) => {
            handleAddCustomer(id)
            setEnableCustomerSelection(false)
          }}
          onClonse={() => setEnableCustomerSelection(false)}
        />
      )}
    </ModalPortal>
  )
}
